// ManageAddressesPage.tsx
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';

interface Address {
  id: string;
  name: string;
  mobileNumber: string;
  pincode: string;
  locality: string;
  addressLine1: string; // Renamed from address (Area and Street)
  city: string; // Renamed from City/District/Town
  state: string;
  landmark?: string;
  alternatePhone?: string;
  addressType: 'Home' | 'Work';
}

interface ManageAddressesPageProps {
  userId: string;
}

export const ManageAddressesPage: React.FC<ManageAddressesPageProps> = ({ userId }) => {
  const [addresses, setAddresses] = useState<Address[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showAddEditForm, setShowAddEditForm] = useState(false);
  const [editingAddress, setEditingAddress] = useState<Address | null>(null);

  // State for the new/edited address form
  const [formData, setFormData] = useState<Partial<Address>>({
    name: '',
    mobileNumber: '',
    pincode: '',
    locality: '',
    addressLine1: '',
    city: '',
    state: '',
    landmark: '',
    alternatePhone: '',
    addressType: 'Home',
  });

  const statesOfIndia = [
    "Andhra Pradesh", "Arunachal Pradesh", "Assam", "Bihar", "Chhattisgarh", "Goa", "Gujarat",
    "Haryana", "Himachal Pradesh", "Jharkhand", "Karnataka", "Kerala", "Madhya Pradesh",
    "Maharashtra", "Manipur", "Meghalaya", "Mizoram", "Nagaland", "Odisha", "Punjab",
    "Rajasthan", "Sikkim", "Tamil Nadu", "Telangana", "Tripura", "Uttar Pradesh",
    "Uttarakhand", "West Bengal", "Andaman and Nicobar Islands", "Chandigarh",
    "Dadra and Nagar Haveli and Daman and Diu", "Lakshadweep", "Delhi", "Puducherry"
  ];

  useEffect(() => {
    fetchAddresses();
  }, [userId]);

  const fetchAddresses = async () => {
    try {
      setLoading(true);
      const response = await axios.get<Address[]>(`https://ayushvatika.com/api/addresses/user/${userId}`);
      setAddresses(response.data);
    } catch (err: any) {
      console.error("Failed to fetch addresses:", err);
      setError(err.response?.data?.message || "Failed to load addresses.");
      toast.error("Failed to load addresses.");
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));

    // If the input is pincode, trigger auto-fill logic
    if (name === "pincode") {
      handlePincodeChange(value);
    }
  };

  const handlePincodeChange = async (pincode: string) => {
    if (pincode.length === 6 && /^\d{6}$/.test(pincode)) {
      setLoading(true);
      setError(null); // Clear any previous errors related to pincode lookup

      try {
        const pincodeApiUrl = `https://api.postalpincode.in/pincode/${pincode}`;
        const response = await axios.get(pincodeApiUrl);
        const data = response.data; // This will be an array as per the API structure

        let fetchedCity = '';
        let fetchedState = '';

        if (data && data.length > 0 && data[0].Status === 'Success' && data[0].PostOffice && data[0].PostOffice.length > 0) {
          const postOffice = data[0].PostOffice[0]; // Take the first PostOffice entry
          fetchedCity = postOffice.District;
          fetchedState = postOffice.State;
          toast.info("City and State auto-filled based on Pincode.");
        } else {
          toast.warn("Pincode not recognized or no data found. Please fill city and state manually.");
          fetchedCity = '';
          fetchedState = '';
        }

        setFormData(prev => ({
          ...prev,
          city: fetchedCity,
          state: fetchedState,
        }));

      } catch (err: any) {
        console.error("Failed to fetch city/state for pincode:", err);
        toast.error("Failed to auto-fill city/state. Please enter manually.");
        setError("Failed to fetch address for pincode. Please enter manually.");
        setFormData(prev => ({ ...prev, city: '', state: '' })); // Clear if error
      } finally {
        setLoading(false);
      }
    } else if (pincode.length < 6 && pincode.length > 0) {
      // Clear city/state if pincode is incomplete or being typed
      setFormData(prev => ({ ...prev, city: '', state: '' }));
      setError(null); // Clear lookup specific error when pincode is incomplete
    }
  };


  const handleAddAddressClick = () => {
    setEditingAddress(null); // Clear any existing editing state
    setFormData({ // Reset form for new address
      name: '', mobileNumber: '', pincode: '', locality: '', addressLine1: '',
      city: '', state: '', landmark: '', alternatePhone: '', addressType: 'Home',
    });
    setShowAddEditForm(true);
  };

  const handleEditAddressClick = (address: Address) => {
    setEditingAddress(address);
    setFormData(address); // Populate form with existing address data
    setShowAddEditForm(true);
  };

  const handleSaveAddress = async (e: React.FormEvent) => {
  e.preventDefault();
  try {
    setLoading(true);

    if (editingAddress && editingAddress.id) {
      // ✅ Update existing address
      await axios.put(
        `https://ayushvatika.com/api/addresses/${editingAddress.id}`,
        formData,
        {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        }
      );
      toast.success("Address updated successfully!");
    } else {
      // ✅ Add new address
      await axios.post(
        `https://ayushvatika.com/api/addresses/user/${userId}`,
        formData,
        {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        }
      );
      toast.success("Address added successfully!");
    }

    setShowAddEditForm(false);
    fetchAddresses(); // Refresh the list of addresses
  } catch (err: any) {
    console.error("Failed to save address:", err);
    toast.error(err.response?.data?.message || "Failed to save address.");
  } finally {
    setLoading(false);
  }
};

const handleDeleteAddress = async (addressId?: string) => {
  if (!addressId) {
    toast.error("Invalid address ID. Cannot delete.");
    return;
  }

  if (window.confirm("Are you sure you want to delete this address?")) {
    try {
      setLoading(true);
      await axios.delete(`https://ayushvatika.com/api/addresses/${addressId}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });
      toast.success("Address deleted successfully!");
      fetchAddresses(); // Refresh the list
    } catch (err: any) {
      console.error("Failed to delete address:", err);
      toast.error(err.response?.data?.message || "Failed to delete address.");
    } finally {
      setLoading(false);
    }
  }
};


  const handleCancel = () => {
    setShowAddEditForm(false);
    setEditingAddress(null);
    setFormData({ // Reset form
      name: '', mobileNumber: '', pincode: '', locality: '', addressLine1: '',
      city: '', state: '', landmark: '', alternatePhone: '', addressType: 'Home',
    });
  };

  const handleUseCurrentLocation = () => {
    if (navigator.geolocation) {
      setLoading(true);
      setError(null); // Clear previous errors
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          const { latitude, longitude } = position.coords;
          toast.info(`Fetching address for Lat: ${latitude}, Lon: ${longitude}`);

          // --- SIMULATED REVERSE GEOCODING ---
          // In a real application, you would make an API call here to a reverse geocoding service.
          // For example, using OpenStreetMap Nominatim API (ensure you adhere to their usage policy):
          // const response = await axios.get(`https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${latitude}&lon=${longitude}`);
          // const addressData = response.data.address;

          // For demonstration, we'll simulate some data based on Noida, UP, India as per your screenshot
          // You would replace this with actual data parsed from your reverse geocoding API response
          const simulatedAddressData = {
            addressLine1: "127, B-block sector-2", // Replace with actual street/area
            locality: "Noida", // Replace with actual locality
            city: "Noida",
            state: "Uttar Pradesh",
            pincode: "201301",
            landmark: "MCO school" // This might be harder to get from reverse geocoding
          };
          // --- END SIMULATED REVERSE GEOCODING ---


          setFormData(prev => ({
            ...prev,
            ...simulatedAddressData,
            // You might want to pre-fill name/mobile if you have them in user profile
            // name: 'Priya Rajput',
            // mobileNumber: '9876543210',
          }));
          setLoading(false);
          toast.success("Location fetched successfully! Please review and confirm address details.");
        },
        (geoError) => {
          setLoading(false);
          console.error("Geolocation error:", geoError);
          let errorMessage = "Failed to get current location.";
          switch (geoError.code) {
            case geoError.PERMISSION_DENIED:
              errorMessage = "Permission to access location was denied. Please enable location services in your browser settings.";
              break;
            case geoError.POSITION_UNAVAILABLE:
              errorMessage = "Location information is unavailable.";
              break;
            case geoError.TIMEOUT:
              errorMessage = "The request to get user location timed out.";
              break;
            default:
              errorMessage = "An unknown error occurred while fetching location.";
              break;
          }
          toast.error(errorMessage);
          setError(errorMessage); // Set the error for display in the form
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 0
        }
      );
    } else {
      toast.error("Geolocation is not supported by your browser.");
      setError("Geolocation is not supported by your browser.");
    }
  };

  if (loading && !showAddEditForm) { // Only show global loading if not in form view
    return (
      <div className="flex justify-center items-center h-full">
        <div className="text-emerald-700 text-lg">Loading addresses...</div>
      </div>
    );
  }

  // Display error only if it's a global error and not related to form actions
  if (error && !showAddEditForm) {
    return (
      <div className="flex flex-col justify-center items-center h-full text-red-600 text-lg">
        <p>{error}</p>
        <button onClick={fetchAddresses} className="mt-4 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600">
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="p-8 space-y-8 bg-white rounded-xl shadow-lg">
      <h2 className="text-2xl font-bold text-gray-800 mb-6">Manage Addresses</h2>

      {!showAddEditForm ? (
        <>
          <button
            onClick={handleAddAddressClick}
            className="flex items-center px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition duration-200"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"></path></svg>
            ADD A NEW ADDRESS
          </button>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {addresses.length === 0 ? (
              <p className="text-gray-600">No addresses added yet.</p>
            ) : (
              addresses.map(address => (
                <div key={address.id} className="border border-gray-200 rounded-lg p-4 relative">
                  <div className="absolute top-2 right-2 flex space-x-2">
                    <button onClick={() => handleEditAddressClick(address)} className="text-blue-600 hover:text-blue-800">
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"></path></svg>
                    </button>
                   <button
  onClick={() => handleDeleteAddress(address.id)}
  className="text-red-600 hover:text-red-800"
>
  <svg
    className="w-5 h-5"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
    ></path>
  </svg>
</button>

                  </div>
                  <span className="text-xs font-semibold text-gray-500 uppercase px-2 py-1 bg-gray-100 rounded-full">{address.addressType}</span>
                  <p className="font-semibold text-gray-800 mt-2">{address.name} <span className="ml-2 text-gray-600 text-sm">{address.mobileNumber}</span></p>
                  <p className="text-gray-700 text-sm">{address.addressLine1}, {address.locality}, {address.city}, {address.state} - {address.pincode}</p>
                  {address.landmark && <p className="text-gray-600 text-xs">Landmark: {address.landmark}</p>}
                </div>
              ))
            )}
          </div>
        </>
      ) : (
        <form onSubmit={handleSaveAddress} className="space-y-4">
          <h3 className="text-xl font-semibold text-gray-800">{editingAddress ? 'Edit Address' : 'Add New Address'}</h3>

          {/* New "Use My Current Location" button */}
          <button
            type="button"
            onClick={handleUseCurrentLocation}
            className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition duration-200"
            disabled={loading} // Disable if loading to prevent multiple clicks
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.828 0L6.343 16.657M17.657 16.657A8 8 0 1118 8a8 8 0 01-1.071 3.514L12 21l-4.929-7.371A8 8 0 0118 8z"></path></svg>
            Use My Current Location
          </button>
          {error && showAddEditForm && ( // Display error specifically for the form if it occurs during location fetch or pincode lookup
            <p className="text-red-600 text-sm">{error}</p>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-gray-600 text-sm mb-1">Name</label>
              <input
                type="text"
                name="name"
                value={formData.name || ''}
                onChange={handleInputChange}
                className="w-full border rounded-lg px-4 py-2 focus:ring-emerald-500 focus:border-emerald-500"
                required
              />
            </div>
            <div>
              <label className="block text-gray-600 text-sm mb-1">10-digit mobile number</label>
              <input
                type="text"
                name="mobileNumber"
                value={formData.mobileNumber || ''}
                onChange={handleInputChange}
                className="w-full border rounded-lg px-4 py-2 focus:ring-emerald-500 focus:border-emerald-500"
                maxLength={10}
                pattern="\d{10}"
                title="Please enter a 10-digit mobile number"
                required
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-gray-600 text-sm mb-1">Pincode</label>
              <input
                type="text"
                name="pincode"
                value={formData.pincode || ''}
                onChange={handleInputChange}
                className="w-full border rounded-lg px-4 py-2 focus:ring-emerald-500 focus:border-emerald-500"
                maxLength={6}
                pattern="\d{6}"
                title="Please enter a 6-digit pincode"
                required
              />
            </div>
            <div>
              <label className="block text-gray-600 text-sm mb-1">Locality</label>
              <input
                type="text"
                name="locality"
                value={formData.locality || ''}
                onChange={handleInputChange}
                className="w-full border rounded-lg px-4 py-2 focus:ring-emerald-500 focus:border-emerald-500"
                required
              />
            </div>
          </div>

          <div>
            <label className="block text-gray-600 text-sm mb-1">Address (Area and Street)</label>
            <input
              type="text"
              name="addressLine1"
              value={formData.addressLine1 || ''}
              onChange={handleInputChange}
              className="w-full border rounded-lg px-4 py-2 focus:ring-emerald-500 focus:border-emerald-500"
              required
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-gray-600 text-sm mb-1">City/District/Town</label>
              <input
                type="text"
                name="city"
                value={formData.city || ''}
                onChange={handleInputChange}
                className="w-full border rounded-lg px-4 py-2 focus:ring-emerald-500 focus:border-emerald-500"
                required
              />
            </div>
            <div>
              <label className="block text-gray-600 text-sm mb-1">State</label>
              <select
                name="state"
                value={formData.state || ''}
                onChange={handleInputChange}
                className="w-full border rounded-lg px-4 py-2 focus:ring-emerald-500 focus:border-emerald-500 bg-white"
                required
              >
                <option value="">--Select State--</option>
                {statesOfIndia.map(state => (
                  <option key={state} value={state}>{state}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-gray-600 text-sm mb-1">Landmark (Optional)</label>
              <input
                type="text"
                name="landmark"
                value={formData.landmark || ''}
                onChange={handleInputChange}
                className="w-full border rounded-lg px-4 py-2 focus:ring-emerald-500 focus:border-emerald-500"
              />
            </div>
            <div>
              <label className="block text-gray-600 text-sm mb-1">Alternate Phone (Optional)</label>
              <input
                type="text"
                name="alternatePhone"
                value={formData.alternatePhone || ''}
                onChange={handleInputChange}
                className="w-full border rounded-lg px-4 py-2 focus:ring-emerald-500 focus:border-emerald-500"
                maxLength={10}
                pattern="\d{10}"
                title="Please enter a 10-digit mobile number"
              />
            </div>
          </div>

          <div>
            <label className="block text-gray-600 text-sm mb-2">Address Type</label>
            <div className="flex space-x-4">
              <label className="inline-flex items-center">
                <input
                  type="radio"
                  name="addressType"
                  value="Home"
                  checked={formData.addressType === 'Home'}
                  onChange={handleInputChange}
                  className="form-radio text-emerald-600"
                />
                <span className="ml-2 text-gray-700">Home</span>
              </label>
              <label className="inline-flex items-center">
                <input
                  type="radio"
                  name="addressType"
                  value="Work"
                  checked={formData.addressType === 'Work'}
                  onChange={handleInputChange}
                  className="form-radio text-emerald-600"
                />
                <span className="ml-2 text-gray-700">Work</span>
              </label>
            </div>
          </div>

          <div className="flex justify-end space-x-4">
            <button
              type="button"
              onClick={handleCancel}
              className="px-6 py-2 bg-gray-300 text-gray-800 rounded-lg hover:bg-gray-400 transition duration-200"
              disabled={loading}
            >
              CANCEL
            </button>
            <button
              type="submit"
              className="px-6 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition duration-200"
              disabled={loading}
            >
              SAVE
            </button>
          </div>
        </form>
      )}
    </div>
  );
};